import { MutableRefObject, useCallback, useContext, useRef } from 'react';

import { StartNewSearchScope } from 'pages/hotels/reducer/AccommodationList/types';

import { useSetAtom } from 'jotai';

import { useSetDestinationLabel, useSetPagination } from 'hooks/form/search';
import { useSetSearchAtom } from 'hooks/form/useSetSearchAtom';
import { useSync } from 'hooks/form/useSync';
import { useSaveLastAirports } from 'hooks/useSaveLastAirports';

import { metaAtom } from 'data/Atom/Meta';
import {
    baseSearchFormAtom,
    currentSearchAtom,
    geoFenceAtom,
} from 'data/Atom/Search';
import { filterStore, searchStore } from 'data/Atom/Store';

import {
    convertSearchToUrlParams,
    searchStepToPathNameMap,
} from 'utility/Convert/Search/searchToUrlParams';
import { convertUrlToSearch } from 'utility/Convert/Search/urlToSearch';
import Console from 'utility/Debug/Console';
import { CommunicationContext } from 'utility/Provide/Context/PageContext';
import { createLastSearchObject } from 'utility/Provide/LastSearch/createLastSearchObject';
import { removeURLParam, replaceParamListInUrl } from 'utility/Provide/Url';

import { SearchFormDefaults } from 'types/legacy/backendInformation';
import { GeoFence, Search } from 'types/Search/form';
import { SemParams } from 'types/Search/params';

import { getSearchAction, SearchAction } from '../__helper/getSearchAction';
import { stringifyObject } from '../__helper/stringify';

const metaUpdateRef: MutableRefObject<boolean> = {
    current: false,
};

const removeSemParams = (urlParams: URLSearchParams) => {
    for (const param of Object.values(SemParams)) {
        removeURLParam(urlParams, param);
    }
};

export const useSearch = () => {
    const namespace = useContext(CommunicationContext);
    const { syncSubStoresWithCurrentSearch } = useSync();

    const setCurrentSearch = useSetSearchAtom(currentSearchAtom);
    const setBaseSearchForm = useSetAtom(baseSearchFormAtom);
    const setPagination = useSetPagination();
    const setDestinationLabel = useSetDestinationLabel();
    const saveLastAirports = useSaveLastAirports();

    const { components, react } = useContext(CommunicationContext);

    const previousSearch = useRef<Search | undefined>();
    const isPopState = useRef(false);
    const currentSearchUrl = useRef<string | undefined>();

    const initialDestinationLabel =
        components.backendInformationFunc.getData<SearchFormDefaults>(
            'searchFormDefaults'
        ).form?.destinationLabel;
    const baseUrl =
        components.backendInformationFunc.getData<string>('baseUrl');

    const dispatchSearch = useCallback(
        (search?: Search) => {
            const currentSearch = search ?? searchStore.get(currentSearchAtom);

            try {
                const params = convertSearchToUrlParams(currentSearch);
                let searchScope = StartNewSearchScope.Default;
                const isNewRegion =
                    stringifyObject(currentSearch.destination.label) !==
                    stringifyObject(previousSearch.current?.destination.label);
                let shouldRemoveSemParams = false;
                components.travelsearch.hotellistDynamicController?.api?.onStartNewSearch(
                    isNewRegion
                );
                saveLastAirports(currentSearch.departureAirportList);

                if (!previousSearch.current?.destination) {
                    searchScope = StartNewSearchScope.Initial;
                } else if (isNewRegion) {
                    searchScope = StartNewSearchScope.Destination;
                    shouldRemoveSemParams = true;
                    if (
                        !currentSearch.destination.hotelId &&
                        !currentSearch.destination.directHotelSearchId
                    ) {
                        components.travel.map.mapService.onStartNewRegionSearch();
                    }
                } else if (
                    currentSearch.pagination?.currentPage !==
                    previousSearch.current?.pagination?.currentPage
                ) {
                    searchScope = StartNewSearchScope.Pagination;
                } else if (
                    // anything else changed in search (filter was set, etc.), hence discard the sem params
                    stringifyObject(currentSearch.sorting) ===
                    stringifyObject(previousSearch?.current?.sorting)
                ) {
                    shouldRemoveSemParams = true;
                }

                if (shouldRemoveSemParams) {
                    removeSemParams(params);
                }

                // dispatch in legacy js
                if (currentSearch.destination.searchStep) {
                    const pathName =
                        searchStepToPathNameMap[
                            currentSearch.destination.searchStep
                        ];
                    const pathNameWithParams = `${pathName}?${params.toString()}`;
                    const searchUrl = `${baseUrl}${pathNameWithParams}`;

                    if (components.lastSearchesDataService) {
                        const lastSearchId =
                            components.lastSearchesDataService.getFormHash(
                                searchUrl,
                                true
                            );
                        // create temp. lastsearch
                        if (lastSearchId) {
                            const ssoId =
                                components.backendInformationFunc.getSsoData()
                                    .ssoId;
                            const lastSearchObject = createLastSearchObject({
                                label: currentSearch.destination.label ?? '',
                                searchUrl,
                                pathNameWithParams,
                                urlDomain: baseUrl,
                                lastSearchId,
                                ssoId,
                            });
                            components.lastSearchesDataService.saveTempItemToLocalStorage(
                                lastSearchObject
                            );
                        }
                    }

                    // change site, if searchStep changes
                    if (
                        previousSearch.current &&
                        currentSearch.destination.searchStep !==
                            previousSearch.current?.destination.searchStep
                    ) {
                        location.href = searchUrl;
                        return false;
                    }

                    // check if not popstate is triggered
                    if (
                        !isPopState.current &&
                        searchScope !== StartNewSearchScope.Initial
                    ) {
                        window.history.pushState(
                            {
                                target: 'hotelSearch',
                                url: searchUrl,
                                destinationLabel:
                                    currentSearch.destination.label,
                            },
                            '',
                            searchUrl
                        );
                        window.dispatchEvent(
                            new CustomEvent('urlChanged', {
                                detail: window.location.href,
                            })
                        );
                    }

                    currentSearchUrl.current = searchUrl;

                    // logout-url update
                    components?.travel?.customerLogin?.updateLogoutUrl?.();
                    react.dispatch('hotel-list-search', {
                        searchUrl,
                        searchScope,
                    });
                }
            } finally {
                previousSearch.current = currentSearch;
                isPopState.current = false;
                namespace.components.jLocalStorage?.updateCurrentLocation();
            }
        },
        [
            baseUrl,
            components.backendInformationFunc,
            components.lastSearchesDataService,
            components.travel?.customerLogin,
            components.travel.map.mapService,
            components.travelsearch.hotellistDynamicController?.api,
            namespace.components.jLocalStorage,
            react,
            saveLastAirports,
        ]
    );

    const onSearchChange = useCallback(() => {
        searchStore.sub(currentSearchAtom, async () => {
            const currentSearch = searchStore.get(currentSearchAtom);
            const searchAction = getSearchAction({
                current: currentSearch,
                previous: previousSearch.current,
            });

            if (searchAction === SearchAction.NewSearch) {
                dispatchSearch();
            }
        });
    }, [dispatchSearch]);

    const onHistoryChange = useCallback(() => {
        // listen to browserBack
        const onPopState = (event: PopStateEvent) => {
            const baseSearchForm = searchStore.get(baseSearchFormAtom);

            if (!event.state || event.state?.target === 'hotelSearch') {
                const searchUrl = event.state?.url ?? location.href;
                const destinationLabel =
                    event.state?.destinationLabel ?? initialDestinationLabel;
                const search = convertUrlToSearch({
                    searchUrl,
                    destinationLabel,
                });

                if (search) {
                    isPopState.current = true;
                    setBaseSearchForm({
                        ...baseSearchForm,
                        current: search,
                    });
                }
            }
        };
        // remove listener before adding a new one
        window.removeEventListener('popstate', onPopState);
        // add listener
        window.addEventListener('popstate', onPopState);
    }, [initialDestinationLabel, setBaseSearchForm]);

    const onGeoFenceChange = useCallback(() => {
        const onChange = (geoFence: GeoFence | undefined) => {
            searchStore.set(geoFenceAtom, geoFence);
        };

        react.unbind('setGeoFence', onChange);
        react.bind('setGeoFence', onChange);
    }, [react]);

    const updateSearch = useCallback(
        (url: string) => {
            if (url != '') {
                const search = convertUrlToSearch({
                    searchUrl: url,
                    destinationLabel: initialDestinationLabel,
                });

                setCurrentSearch(search);
            }
        },
        [initialDestinationLabel, setCurrentSearch]
    );

    const subToSearchStore = useCallback(() => {
        // sync the sub stores with global searchStore
        searchStore.sub(currentSearchAtom, () => {
            syncSubStoresWithCurrentSearch(metaUpdateRef.current);
        });
        //sync filter information on outer change
        searchStore.sub(metaAtom, () => {
            Console.trace('update filter meta data');
            filterStore.set(metaAtom, searchStore.get(metaAtom));
        });
    }, [syncSubStoresWithCurrentSearch]);

    const initSearch = useCallback(
        (url: string) => {
            if (url != '') {
                // init subscription to searchStore
                subToSearchStore();
                // update search according given url
                updateSearch(url);
                // init change listener;
                onSearchChange();
                onHistoryChange();
                onGeoFenceChange();
                // initially start a new search with defaults
                dispatchSearch();
            }
        },
        [
            dispatchSearch,
            onGeoFenceChange,
            onHistoryChange,
            onSearchChange,
            subToSearchStore,
            updateSearch,
        ]
    );

    const updateSearchMeta = useCallback(
        (pageCount: number, destinationLabel?: string) => {
            metaUpdateRef.current = true;
            try {
                setPagination((current) => ({
                    ...current,
                    pageCount,
                }));
                destinationLabel && setDestinationLabel(destinationLabel);
            } finally {
                metaUpdateRef.current = false;
            }
        },
        [setDestinationLabel, setPagination]
    );

    const removeParamInSearch = useCallback(
        (params: string[]) => {
            const paramList = params.map((param: string) => ({
                key: param,
                value: '',
            }));
            const newUrl = replaceParamListInUrl(
                window.location.href,
                paramList
            );

            updateSearch(newUrl);
        },
        [updateSearch]
    );

    return {
        initSearch,
        updateSearchMeta,
        removeParamInSearch,
    };
};
